import React, { useState, useEffect, useRef, useCallback } from "react";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

import { useTranslation } from "react-i18next";

import { useDebounce } from "../../../../hooks/useDebounce";
import useCRUDService from "../../../../hooks/useCRUDService";
import useApi from "../../../../hooks/useApi";
import CreditsForm from "./creditsForm";
import Layout from "../../../../layout/layout";
import Header from "../../../../layout/header";
import useSkeleton from "../../../../hooks/useSkeleton";

import helperFormats from "../../../../utils/helperFormats";

import { atom, useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";
import { creditIdState } from "../../../../states/creditIdState";

const Credits = () => {
  const [isLoading, setIsLoading] = useAtom(loadingState);
  const [creditId, setCreditId] = useAtom(creditIdState);

  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("credits");

  const [entities, setEntities] = useState(null);
  const [formDialog, setFormDialog] = useState(false);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "code",
          type: "string",
        },
        {
          name: "openedDate",
          type: "date",
        },
        {
          name: "application.beneficiary_DocumentNumber",
          type: "string",
        },
        {
          name: "application.beneficiary_Name",
          type: "string",
        },
        {
          name: "application.beneficiary_LastName",
          type: "string",
        },
        {
          name: "application.amount",
          type: "number",
        },
        {
          name: "application.installmentsAmount",
          type: "number",
        },
        {
          name: "application.installmentsNumber",
          type: "number",
        },
        {
          name: "application.annualRate",
          type: "number",
        },
        {
          name: "application.annualDefaultRate",
          type: "number",
        },
        {
          name: "application.administrationFeeRate",
          type: "number",
        },
      ]);

      const response = await CRUDService.getEntities(
        columns,
        first,
        rows,
        sortField ?? "application.beneficiary_DocumentNumber",
        sortOrder >= 0 ? "ASC" : "DESC",
        debouncedSearchTerm
      );

      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm, CRUDService]);

  useEffect(() => {
    fetchData();
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm]);

  const openFormDialog = async (formMode, selectedCredits) => {
    setCreditId(selectedCredits?.id);

    setFormSettings((prev) => ({
      ...prev,
      formMode: formMode,
      entityId: selectedCredits?.id,
      hideDialog: hideDialog,
      toast: toast,
      CRUDService: CRUDService,
    }));

    setFormDialog(true);
  };

  const hideDialog = (action) => {
    if (action !== "CANCEL") {
      setFormDialog(false);
      // Restablece todos los filtros a sus valores predeterminados
      setFirst(0);
      setRows(100);
      setSortField(null);
      setSortOrder(null);
      setGlobalFilter("");
      // Restablece el valor del campo de entrada
      if (searchInput.current) {
        searchInput.current.value = "";
      }
      // Luego, realiza una nueva búsqueda con los filtros restablecidos
      fetchData();
    }

    setFormDialog(false);
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const [formSettings, setFormSettings] = useState({
    entityId: -1,
    formMode: "",
    hideDialog: hideDialog,
    toast: toast,
    CRUDService: CRUDService,
  });

  const actions = (
    <React.Fragment>
      <Button
        label={t("DataTable.New")}
        icon="pi pi-plus"
        className="p-button-primary"
        onClick={() => openFormDialog("CREATE")}
      />
    </React.Fragment>
  );

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex justify-content-center">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success mr-2"
          onClick={() => openFormDialog("UPDATE", rowData)}
        />
      </div>
    );
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  const getDialogTitle = () => {
    let action = "Add";

    if (formSettings.formMode == "DELETE") {
      action = "Delete";
    } else if (formSettings.formMode == "UPDATE") {
      action = "Edit";
    }

    return `${t(action)} ${t("entities.credits.singular")}`;
  };

  const getDialogHeader = () => {
    if (formSettings.formMode === "DELETE") {
      return getDialogTitle();
    }

    return (
      <div className="abm-form-hero">
        <h2 className="abm-form-hero-title">{getDialogTitle()}</h2>
        <p className="abm-form-hero-subtitle">
          Complete la información para gestionar el registro.
        </p>
      </div>
    );
  };

  return (
    <Layout
      header={
        <Header
          icon={<i className="pi pi-credit-card"></i>}
          title={t("entities.credits.plural")}
          actions={actions}
        />
      }
    >
      <Toast ref={toast} />
      <div className={s.get("dataTableContainer")}>
        <DataTable
          onPage={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          onSort={(e) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          ref={dt}
          value={entities}
          lazy
          paginator
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          columnResizeMode="fit"
          showGridlines
          dataKey="id"
          rowsPerPageOptions={[10, 25, 100, 1000]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate={`${t(
            "DataTable.currentPageReportTemplate"
          )} ${t("Credits")}`}
          globalFilter={globalFilter}
          header={header}
          className="flex flex-column w-full"
        >
          <Column
            field="code"
            header={t("entities.credits.code")}
            bodyStyle={{ textAlign: "center" }}
            sortable
          ></Column>
          <Column
            field="openedDate"
            header={t("entities.credits.openedDate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) =>
              helperFormats.isDate(rowData.openedDate)
                ? helperFormats.formatDate(new Date(rowData.openedDate))
                : "Pendiente"
            }
            sortable
          ></Column>
          <Column
            field="application.beneficiary_DocumentNumber"
            bodyStyle={{ textAlign: "center" }}
            header={t(
              "entities.credits.application.beneficiary_documentNumber"
            )}
            sortable
          ></Column>
          <Column
            field="application.beneficiary_Name"
            header={t("entities.credits.application.beneficiary_name")}
            sortable
          ></Column>
          <Column
            field="application.beneficiary_LastName"
            header={t("entities.credits.application.beneficiary_lastName")}
            sortable
          ></Column>
          <Column
            field="application.amount"
            header={t("entities.credits.application.amount")}
            bodyStyle={{ textAlign: "right" }}
            body={(rowData) => {
              return helperFormats.formatDecimal(rowData.application.amount);
            }}
            sortable
          ></Column>
          <Column
            field="application.installmentsAmount"
            header={t("entities.credits.application.installmentsAmount")}
            bodyStyle={{ textAlign: "right" }}
            body={(rowData) => {
              return helperFormats.formatDecimal(
                rowData.application.installmentsAmount
              );
            }}
            sortable
          ></Column>
          <Column
            field="application.installmentsNumber"
            bodyStyle={{ textAlign: "center" }}
            header={t("entities.credits.application.installmentsNumber")}
            sortable
          ></Column>
          <Column
            field="application.annualRate"
            header={t("entities.credits.application.annualRate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) => {
              return (
                helperFormats
                  .formatDecimal(rowData.application.annualRate * 100, 0, 0)
                  .toString() + "%"
              );
            }}
            sortable
          ></Column>
          <Column
            field="application.annualDefaultRate"
            header={t("entities.credits.application.annualDefaultRate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) => {
              return (
                helperFormats
                  .formatDecimal(
                    rowData.application.annualDefaultRate * 100,
                    0,
                    0
                  )
                  .toString() + "%"
              );
            }}
            sortable
          ></Column>
          <Column
            field="application.administrationFeeRate"
            header={t("entities.credits.application.administrationFeeRate")}
            bodyStyle={{ textAlign: "center" }}
            body={(rowData) => {
              return (
                helperFormats
                  .formatDecimal(
                    rowData.application.administrationFeeRate * 100,
                    0,
                    0
                  )
                  .toString() + "%"
              );
            }}
            sortable
          ></Column>
          <Column
            body={actionBodyTemplate}
            exportable={false}
            style={{ width: "8rem", textAlign: "center" }}
          ></Column>
        </DataTable>
        <React.Fragment>
          <Dialog
            visible={formDialog}
            header={getDialogHeader()}
            modal
            className="flex w-9"
            onHide={hideDialog}
          >
            <CreditsForm settings={formSettings} />
          </Dialog>
        </React.Fragment>
      </div>
    </Layout>
  );
};

export default Credits;
